import { User, AskRequestComment as Comment } from '@hone-automation/common';

/**
 * Extracts all unique users from comments, including comment authors and tagged users.
 * This includes both active and deleted/deactivated users.
 *
 * @param comments - Array of comments to extract users from
 * @returns Array of unique users found in the comments
 */
export const extractUsersFromComments = (comments?: Comment[]): User[] => {
  if (!comments) return [];

  const userMap = new Map<string, User>();

  comments.forEach(comment => {
    // Add the comment author
    if (comment.user && comment.user.userId) {
      userMap.set(comment.user.userId, comment.user);
    }

    // Add tagged users (includes deleted/deactivated users)
    const commentAny = comment as any;
    if (commentAny.taggedUsers && Array.isArray(commentAny.taggedUsers)) {
      commentAny.taggedUsers.forEach((taggedUser: any) => {
        if (taggedUser.userId) {
          userMap.set(taggedUser.userId, taggedUser);
        }
      });
    }
  });

  return Array.from(userMap.values());
};

/**
 * Merges active users from a location with users extracted from comments.
 * Active users take priority. Users only found in comments (deactivated) are marked with deleted: true.
 *
 * @param activeUsers - Array of active users from the location
 * @param usersFromComments - Array of users extracted from comments (may include deleted users)
 * @returns Merged array of all users with deleted flag for deactivated users
 */
export const mergeActiveAndCommentUsers = (activeUsers?: User[], usersFromComments: User[] = []): User[] => {
  const userMap = new Map<string, User>();

  // First add all active users from the location
  activeUsers?.forEach(user => {
    if (user.userId) {
      userMap.set(user.userId, user);
    }
  });

  // Then add users from comments that are not in fetched users (deactivated users)
  usersFromComments.forEach(user => {
    if (user.userId && !userMap.has(user.userId)) {
      userMap.set(user.userId, { ...user, deleted: true } as User);
    }
  });

  return Array.from(userMap.values());
};
